import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo, useEffect, useRef } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import { apiRequest, parseErrorMessage, parseFieldErrors, isUpgradeRequiredError } from "@/lib/queryClient";
import { InlineUpgradePanel } from "@/components/inline-upgrade-panel";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertMaintenanceTaskSchema, MAINTENANCE_CATEGORIES, type MaintenanceTask, type MaintenanceTaskWithLinkedChats, type MaintenanceSettings, type Property, type TeamMember, type Vendor, type Appliance, type OwnerVendorApproval } from "@shared/schema";
import { isTaskPaidInMonth } from "@shared/task-payment-buckets";
import { sortProperties } from "@/lib/property-utils";
import { PadsplitTaskEmblem } from "@/components/padsplit-task-emblem";
import { priorityWithSla } from "@/lib/sla";
import { z } from "zod";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, RefreshCw, Settings, ChevronDown, ChevronUp, Building2, AlertCircle, Clock, AlertTriangle, CheckCircle2, MessageSquare, CreditCard, ArrowRight, Columns3, Info, Wand2, Loader2, List, LayoutGrid, Timer, ArrowUpDown, ClipboardCheck, SprayCan } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, isPast, isToday, differenceInHours, subMonths } from "date-fns";
import MaintenanceTaskDetail, { isGenericTaskTitle } from "@/components/maintenance-task-detail";
import { ApproveAndPayModal } from "@/components/approve-and-pay-modal";
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";
import { BackToPropertyLink } from "@/components/back-to-property-link";
import { BatchPoolPanel } from "@/components/tasks/batch-pool-panel";
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { useSearchQueryParam, useSearchFilterOverride } from "@/hooks/use-search-query-param";
import { matchesSearch } from "@/lib/global-search";

// Payment-lifecycle statuses: vendor-driven, host cannot drag tasks into these.
const PAYMENT_PENDING_STATUSES = ["completed", "awaiting_approval", "approved"] as const;
const PAID_STATUSES = ["paid"] as const;
const PAYMENT_DRIVEN_STATUSES: string[] = [...PAYMENT_PENDING_STATUSES, ...PAID_STATUSES];
// Task #4284 — only tasks in the Complete or Completed/Payment-Pending columns may
// be dragged into Paid / Closed to open the close popup. Dragging from any other
// column (Open, In Progress, etc.) into Paid / Closed is rejected.
const CLOSABLE_SOURCE_STATUSES: string[] = ["complete", ...PAYMENT_PENDING_STATUSES];

const COLUMNS = [
  { id: "requests",        label: "Pending",                         color: "text-gray-700",    statuses: ["requests"],                                            draggableTarget: true  },
  { id: "open",            label: "Open",                            color: "text-blue-700",    statuses: ["open", "assigned", "accepted"],                        draggableTarget: true  },
  { id: "in_progress",     label: "In Progress",                     color: "text-yellow-700",  statuses: ["in_progress"],                                         draggableTarget: true  },
  { id: "on_hold",         label: "On Hold",                         color: "text-red-600",     statuses: ["on_hold", "needs_attention"],                          draggableTarget: true  },
  { id: "complete",        label: "Complete",                        color: "text-green-700",   statuses: ["complete"],                                            draggableTarget: true  },
  { id: "payment_pending", label: "Completed / Payment Pending",     color: "text-purple-700",  statuses: [...PAYMENT_PENDING_STATUSES] as string[],               draggableTarget: false },
  { id: "paid_closed",     label: "Paid / Closed",                   color: "text-emerald-700", statuses: [...PAID_STATUSES] as string[],                          draggableTarget: false },
];

const HIDDEN_COLUMNS_STORAGE_KEY = "padpulse:tasks:hidden-columns";

const createTaskSchema = insertMaintenanceTaskSchema.omit({ userId: true, createdById: true }).extend({
  dueDate: z.string().optional().nullable(),
  propertyId: z.number().optional().nullable(),
  assigneeId: z.number().optional().nullable(),
  vendorId: z.number().optional().nullable(),
  reporterMemberId: z.number().optional().nullable(),
  linkedApplianceId: z.number().optional().nullable(),
  syncToPadsplit: z.boolean().default(true),
});

type ReporterMember = { id: number; name: string; displayName?: string | null; email?: string | null };

type CreateTaskForm = z.infer<typeof createTaskSchema>;

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-100 text-red-800",
  high: "bg-orange-100 text-orange-800",
  medium: "bg-yellow-100 text-yellow-800",
  low: "bg-green-100 text-green-800",
  batch: "bg-slate-100 text-slate-700",
};

const TERMINAL_STATUSES = ["complete", "completed", "awaiting_approval", "approved", "paid", "rejected", "cancelled"];

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function getSlaIndicator(task: MaintenanceTask, slaConfig?: { urgent: number; high: number; medium: number; low: number } | null) {
  if (!slaConfig || TERMINAL_STATUSES.includes(task.status)) return null;
  const slaHours = slaConfig[task.priority as keyof typeof slaConfig];
  if (!slaHours) return null;
  const elapsed = differenceInHours(new Date(), new Date(task.createdAt));
  if (elapsed >= slaHours) return "overdue";
  if (elapsed >= slaHours * 0.75) return "warning";
  return null;
}

function getDueDateIndicator(task: MaintenanceTask) {
  if (!task.dueDate || TERMINAL_STATUSES.includes(task.status)) return null;
  const due = new Date(task.dueDate);
  if (isPast(due) && !isToday(due)) return "overdue";
  if (isToday(due)) return "today";
  return null;
}

// Task #4447 — compact "age since started" label (time since the task was
// created). <1h collapses to "<1h"; under a day shows hours; otherwise shows
// "Nd" plus the remaining hours when non-zero.
function formatTaskAge(hours: number): string {
  if (hours < 1) return "<1h";
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
}

// Returns the age label plus the SLA-derived color state so a card/row can show
// how long a task has been open relative to its SLA window. Reuses
// `getSlaIndicator` so the thresholds (overdue / warning) stay in lockstep with
// the SLA badge. Returns null for terminal tasks and batch-priority tasks
// (which have no SLA window).
function getTaskAge(
  task: MaintenanceTask,
  slaConfig?: { urgent: number; high: number; medium: number; low: number } | null,
): { label: string; state: "neutral" | "warning" | "overdue" } | null {
  if (TERMINAL_STATUSES.includes(task.status)) return null;
  if (task.priority === "batch") return null;
  const elapsed = differenceInHours(new Date(), new Date(task.createdAt));
  const state = getSlaIndicator(task, slaConfig) ?? "neutral";
  return { label: formatTaskAge(Math.max(0, elapsed)), state };
}

const TASK_AGE_COLORS: Record<"neutral" | "warning" | "overdue", string> = {
  neutral: "bg-gray-100 text-gray-600",
  warning: "bg-orange-100 text-orange-700",
  overdue: "bg-red-100 text-red-700",
};

function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h > 0 ? `${h}h ` : ""}${m}m ${sec}s`;
}

// Live, self-ticking elapsed-time readout for a task with an open work session.
// Mirrors the vendor-portal timer: starts from the open entry's startedAt and
// re-renders every second; cleans up its interval on unmount / prop change.
function LiveTimer({ startedAt, className, testId }: { startedAt: string; className?: string; testId?: string }) {
  const [elapsed, setElapsed] = useState(() =>
    Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)),
  );
  useEffect(() => {
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return (
    <span className={className} data-testid={testId}>
      {formatElapsed(elapsed)}
    </span>
  );
}

// Friendly column-style label for a task's status (board column label), used by
// the list/table view.
function getStatusLabel(status: string): string {
  const col = COLUMNS.find((c) => c.statuses.includes(status));
  return col ? col.label : status;
}

function isTaskTimerActive(task: MaintenanceTaskWithLinkedChats): boolean {
  return !!task.activeTimerStartedAt && !TERMINAL_STATUSES.includes(task.status);
}

function TaskCard({ task, properties, teamMembers, membersById, slaConfig, ownerApproval, onClick }: {
  task: MaintenanceTaskWithLinkedChats;
  properties: Property[];
  teamMembers: TeamMember[];
  membersById: Map<number, ReporterMember>;
  slaConfig?: { urgent: number; high: number; medium: number; low: number } | null;
  ownerApproval?: OwnerVendorApproval | null;
  onClick: () => void;
}) {
  const property = task.propertyId ? properties.find((p) => p.id === task.propertyId) : null;
  const assignee = task.assigneeId != null ? teamMembers.find((m) => m.id === task.assigneeId) : null;
  const reporter = task.reporterMemberId != null ? membersById.get(task.reporterMemberId) : null;
  const reporterLabel = reporter ? (reporter.displayName || reporter.name || reporter.email || "Member") : null;
  const reporterInitials = reporterLabel ? reporterLabel[0].toUpperCase() : null;
  const assigneeLabel = assignee ? (assignee.name || assignee.email || "U") : null;
  const assigneeInitials = assigneeLabel ? assigneeLabel[0].toUpperCase() : null;
  const slaIndicator = getSlaIndicator(task, slaConfig);
  const dueDateIndicator = getDueDateIndicator(task);
  const age = getTaskAge(task, slaConfig);
  const timerActive = isTaskTimerActive(task);

  const isRoomTurn = task.category === "Room Turn";

  // Active work-timer card treatment wins over the room-turn / SLA borders so a
  // vendor actively working a task is unmistakable on the board.
  const containerClass = timerActive
    ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-300"
    : `${isRoomTurn ? "bg-brand-muted" : "bg-white"} ${slaIndicator === "overdue" ? "border-red-300" : slaIndicator === "warning" ? "border-orange-300" : isRoomTurn ? "border-brand" : "border-gray-200"}`;

  return (
    <div
      className={`border rounded-lg p-4 mb-3 cursor-pointer hover:shadow-md transition-shadow ${containerClass}`}
      onClick={onClick}
      data-testid={`card-maintenance-task-${task.id}`}
      data-tour="maintenance-tasks"
      data-timer-active={timerActive ? "true" : "false"}
    >
      <div className={`flex items-center justify-between text-xs mb-2 ${isRoomTurn ? "text-brand-muted-foreground/70 dark:text-brand-muted-foreground/70" : "text-gray-400"}`}>
        <span data-testid={`text-task-id-${task.id}`}>ID: {task.id}</span>
        <span className="flex items-center gap-1.5">
          {age && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none ${TASK_AGE_COLORS[age.state]}`}
                  data-testid={`text-task-age-${task.id}`}
                >
                  <Clock className="h-2.5 w-2.5" />
                  {age.label}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {age.state === "overdue"
                  ? "Past its SLA window"
                  : age.state === "warning"
                    ? "Approaching its SLA window"
                    : "Within its SLA window"}{" "}
                — open for {age.label}
              </TooltipContent>
            </Tooltip>
          )}
          <span>Created on {format(new Date(task.createdAt), "MMM d, yyyy")}</span>
        </span>
      </div>
      {timerActive && task.activeTimerStartedAt && (
        <div
          className="flex items-center gap-1.5 mb-2 text-xs font-medium text-emerald-700 dark:text-emerald-300"
          data-testid={`active-timer-${task.id}`}
        >
          <Timer className="h-3.5 w-3.5" />
          <span>Working since {format(new Date(task.activeTimerStartedAt), "h:mm a")}</span>
          <span aria-hidden="true">·</span>
          <LiveTimer
            startedAt={task.activeTimerStartedAt}
            className="font-mono font-semibold"
            testId={`live-timer-${task.id}`}
          />
        </div>
      )}
      {property && (
        <p className={`text-sm font-medium mb-1 break-words ${isRoomTurn ? "text-brand dark:text-brand-muted-foreground" : "text-gray-900"}`} data-testid={`text-task-property-${task.id}`}>{property.address || property.name}</p>
      )}
      {task.category && (
        <p className={`text-xs mb-1 ${isRoomTurn ? "text-brand dark:text-brand-muted-foreground" : "text-gray-500"}`} data-testid={`text-task-category-${task.id}`}>{task.category}</p>
      )}
      <p className={`text-sm line-clamp-2 mb-2 ${isRoomTurn ? "text-brand/90 dark:text-brand-muted-foreground/90" : "text-gray-600"}`} data-testid={`text-task-desc-${task.id}`}>
        {task.description || task.title}
      </p>
      <div className="flex flex-col gap-2 mt-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-1.5 flex-wrap">
          <Badge className={`text-xs ${PRIORITY_COLORS[task.priority]}`} data-testid={`badge-priority-${task.id}`}>
            {task.priority}
          </Badge>
          {task.source !== "manual" && (
            <Badge variant="outline" className="text-xs" data-testid={`badge-source-${task.id}`}>{task.source}</Badge>
          )}
          {task.padsplitTaskId && (
            <PadsplitTaskEmblem
              padsplitTaskId={task.padsplitTaskId}
              padsplitPropertyId={property?.padsplitPropertyId}
              testId={`badge-padsplit-synced-${task.id}`}
            />
          )}
          {task.unreadVendorMessageCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-semibold leading-none border border-purple-200"
                  data-testid={`badge-unread-vendor-${task.id}`}
                >
                  <MessageSquare className="h-3 w-3" />
                  {task.unreadVendorMessageCount}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {task.unreadVendorMessageCount === 1 ? "1 unread vendor message" : `${task.unreadVendorMessageCount} unread vendor messages`}
              </TooltipContent>
            </Tooltip>
          )}
          {task.linkedChatCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-blue-50 dark:bg-blue-950/40 text-brand dark:text-blue-300 text-[10px] font-medium leading-none border border-blue-100 dark:border-blue-900"
                  data-testid={`badge-linked-chats-${task.id}`}
                >
                  <MessageSquare className="h-3 w-3" />
                  {task.linkedChatCount}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {task.linkedChatCount === 1 ? "1 linked message" : `${task.linkedChatCount} linked messages`}
              </TooltipContent>
            </Tooltip>
          )}
          {task.dueDate && (
            <div className={`flex items-center gap-1 text-xs ${dueDateIndicator === "overdue" ? "text-red-600 font-medium" : dueDateIndicator === "today" ? "text-orange-600 font-medium" : "text-gray-500"}`}>
              {dueDateIndicator === "overdue" ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
              <span>{format(new Date(task.dueDate), "MMM d")}</span>
            </div>
          )}
          {!TERMINAL_STATUSES.includes(task.status) && task.lockedRateType && task.lockedRateCents != null && task.lockedRateCents > 0 && (() => {
            const isHourly = task.lockedRateType === "hourly";
            const travel = task.travelFeeCents ?? 0;
            const labor = isHourly ? (task.estimateCents ?? 0) : (task.lockedRateCents ?? 0);
            const total = labor + travel;
            if (total <= 0) return null;
            const formatted = `$${(total / 100).toFixed(2)}`;
            const label = isHourly ? `~${formatted} est.` : `${formatted} est.`;
            const tooltip = isHourly
              ? `Vendor estimate (hourly): ${formatted}${task.estimateHours ? ` for ~${task.estimateHours} hrs` : ""}${travel > 0 ? ` incl. $${(travel / 100).toFixed(2)} travel` : ""}. Final charge depends on actual hours worked.`
              : `Vendor estimate (flat): ${formatted}${travel > 0 ? ` incl. $${(travel / 100).toFixed(2)} travel` : ""}.`;
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="text-xs border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"
                    data-testid={`badge-estimate-${task.id}`}
                  >
                    {label}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            );
          })()}
          {TERMINAL_STATUSES.includes(task.status) && task.priceCents != null && task.priceCents > 0 && (
            <Badge
              variant="outline"
              className="text-xs border-green-200 bg-green-50 text-green-700"
              data-testid={`badge-final-${task.id}`}
            >
              ${(task.priceCents / 100).toFixed(2)}
            </Badge>
          )}
          {PAYMENT_PENDING_STATUSES.includes(task.status as any) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="text-xs border-purple-200 bg-purple-50 text-purple-700"
                  data-testid={`badge-payment-pending-${task.id}`}
                >
                  {task.status === "approved" ? "Payment Processing" : "Payment Pending"}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {task.status === "approved"
                  ? "Approved — payment is being processed"
                  : task.status === "awaiting_approval"
                  ? "Work complete — awaiting host approval"
                  : "Work complete — awaiting host approval"}
              </TooltipContent>
            </Tooltip>
          )}
          {PAID_STATUSES.includes(task.status as any) && (
            <Badge
              variant="outline"
              className="text-xs border-emerald-200 bg-emerald-50 text-emerald-700"
              data-testid={`badge-paid-${task.id}`}
            >
              Paid
            </Badge>
          )}
          {slaIndicator && (
            <Badge className={slaIndicator === "overdue" ? "bg-red-100 text-red-700 text-xs" : "bg-orange-100 text-orange-700 text-xs"} data-testid={`badge-sla-${task.id}`}>
              SLA {slaIndicator === "overdue" ? "Overdue" : "Warning"}
            </Badge>
          )}
          {ownerApproval && (
            <Badge
              className={`text-xs ${
                ownerApproval.status === "approved"
                  ? "bg-green-100 text-green-800"
                  : ownerApproval.status === "rejected"
                    ? "bg-red-100 text-red-800"
                    : "bg-yellow-100 text-yellow-800"
              }`}
              data-testid={`badge-owner-approval-${task.id}`}
            >
              {ownerApproval.status === "approved"
                ? "Owner approved"
                : ownerApproval.status === "rejected"
                  ? "Owner rejected"
                  : "Owner approval pending"}
            </Badge>
          )}
        </div>
        {(reporter || assignee) && (
          <div className="flex flex-row flex-wrap gap-x-3 gap-y-1 shrink-0 sm:flex-col sm:gap-x-0 sm:gap-y-1">
            {reporter && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wide text-gray-400">Reported by</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="h-7 w-7 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
                      aria-label={`Reported by ${reporterLabel}`}
                      data-testid={`avatar-reporter-${task.id}`}
                    >
                      {reporterInitials}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Reported by {reporterLabel}</TooltipContent>
                </Tooltip>
              </div>
            )}
            {assignee && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wide text-gray-400">Assigned to</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="h-7 w-7 rounded-full bg-brand flex items-center justify-center text-white text-xs font-medium focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
                      aria-label={`Assigned to ${assigneeLabel}`}
                      data-testid={`avatar-assignee-${task.id}`}
                    >
                      {assigneeInitials}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Assigned to {assigneeLabel}</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        )}
      </div>
      {PAYMENT_PENDING_STATUSES.includes(task.status as any) && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {task.status === "approved" ? (
            <Link href={`/app/operations/approvals?tab=history&task=${task.id}`}>
              <a
                className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900 hover:underline"
                onClick={(e) => e.stopPropagation()}
                data-testid={`link-view-payment-status-${task.id}`}
              >
                <CreditCard className="h-3.5 w-3.5" />
                View Payment Status
                <ArrowRight className="h-3 w-3" />
              </a>
            </Link>
          ) : (
            <Link href={`/app/operations/approvals?task=${task.id}`}>
              <a
                className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900 hover:underline"
                onClick={(e) => e.stopPropagation()}
                data-testid={`link-approve-and-pay-${task.id}`}
              >
                <CreditCard className="h-3.5 w-3.5" />
                Approve &amp; Pay
                <ArrowRight className="h-3 w-3" />
              </a>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default function AppMaintenance() {
  const queryClient = useQueryClient();
  usePageTitle("Tasks");
  const [location, navigateLoc] = useLocation();
  // wouter's useLocation only reflects the path, not the search string. We
  // also subscribe to the search string so URL changes that only touch the
  // query (e.g. notification → /app/maintenance?task=N while already on
  // /app/maintenance?task=M) re-fire the deep-link reader effect below.
  const search = useSearch();
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("maintenance");
  const [createOpen, setCreateOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const params = new URLSearchParams(search);
  const initialPropertyId = params.get("propertyId");
  // Accept both `task` (canonical) and `taskId` (used by deep-links from
  // /app/messages workflow pills) so a clicked ticket pill always lands in
  // the task detail sheet, not just on the maintenance list.
  const initialTaskId = params.get("task") ?? params.get("taskId");
  const initialCommentId = params.get("comment");


  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(
    initialTaskId ? parseInt(initialTaskId, 10) : null
  );
  useEffect(() => {
    setSelectedTaskId(initialTaskId && /^\d+$/.test(initialTaskId) ? Number(initialTaskId) : null);
  }, [initialTaskId]);
  // Comment id to scroll to + briefly highlight when arriving from a mention
  // bell notification (deep link → ?comment=N). Matches the thread-note UX.
  const [focusCommentId, setFocusCommentId] = useState<number | null>(
    initialCommentId && /^\d+$/.test(initialCommentId) ? parseInt(initialCommentId, 10) : null
  );
  // Re-read ?task=/?taskId= whenever the URL changes so dashboard
  // notifications opening /app/maintenance?task=N (via wouter navigate)
  // open the correct task even if the maintenance page is already mounted.
  // Without this, useState's initial-value snapshot wins and the click
  // appears to do nothing.
  useEffect(() => {
    const p = new URLSearchParams(search);
    const t = p.get("task") ?? p.get("taskId");
    if (t && /^\d+$/.test(t)) {
      setSelectedTaskId(parseInt(t, 10));
    }
    const c = p.get("comment");
    setFocusCommentId(c && /^\d+$/.test(c) ? parseInt(c, 10) : null);
    // Task #2814 — Opening a task-comment mention deep link clears the matching
    // bell notification (fires for both fresh loads and an already-mounted page,
    // and for non-bell entry like a direct URL or refresh). Best-effort: never
    // block the deep link if the mark-read call fails.
    if (c && /^\d+$/.test(c)) {
      apiRequest("PATCH", "/api/app/notifications/read-mention", { commentId: parseInt(c, 10) })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
          queryClient.invalidateQueries({ queryKey: ["/api/app/notifications/count"] });
        })
        .catch(() => {});
    }
  }, [location, search]);
  const propertyFilter = params.get("propertyId") || "all";
  const setPropertyFilter = (v: string) => {
    const next = new URLSearchParams(search);
    if (v === "all") next.delete("propertyId");
    else next.set("propertyId", v);
    const qs = next.toString();
    navigateLoc(qs ? `${location}?${qs}` : location, { replace: true });
  };
  const [searchText, setSearchText, incomingSearch] = useSearchQueryParam();
  const [categoryFilter, setCategoryFilter] = useSearchFilterOverride(incomingSearch, useLocalStorageState(
    "pp:tasks-category-filter",
    "all",
  ), "all", "tasks:category-override");
  const [assigneeFilter, setAssigneeFilter] = useSearchFilterOverride(incomingSearch, useLocalStorageState(
    "pp:tasks-assignee-filter",
    "all",
  ), "all", "tasks:assignee-override");
  // Task #4447 — a single persisted "quick filter" selector replaces the old
  // boolean room-turns toggle: "all" | "room_turns" | "active". The legacy
  // `pp:tasks-room-turns-only` key is migrated below so existing hosts who had
  // Room-Turns-only selected keep that selection.
  const [quickFilter, setQuickFilter] = useSearchFilterOverride(incomingSearch, useLocalStorageState<"all" | "room_turns" | "active" | "forms" | "cleans">(
    "pp:tasks-quick-filter",
    (() => {
      if (typeof window === "undefined") return "all";
      if (window.localStorage.getItem("pp:tasks-quick-filter") === null
        && window.localStorage.getItem("pp:tasks-room-turns-only") === "true") {
        return "room_turns";
      }
      return "all";
    })(),
    (s) => (s === "room_turns" || s === "active" || s === "forms" || s === "cleans" ? s : "all"),
  ), "all", "tasks:quick-override");
  const roomTurnsOnly = quickFilter === "room_turns";
  const activeOnly = quickFilter === "active";
  // Forms-on-tasks — form-tasks only (tasks carrying a form template).
  const formsOnly = quickFilter === "forms";
  // Member Cleaning System — scheduled third-party cleaner visits.
  const cleansOnly = quickFilter === "cleans";
  // Task #4447 — board (kanban) vs list (table) view, persisted across reloads.
  const [view, setView] = useLocalStorageState<"board" | "list">(
    "pp:tasks-view",
    "board",
    (s) => (s === "list" ? "list" : "board"),
  );
  // Task #4457 — task ordering applied to both the board columns and the list
  // view, persisted like the view/quick-filter choices. "oldest" (createdAt
  // asc) stays the default; "newest" flips it; "due" triages by due date;
  // "age" surfaces SLA breaches first (deepest into the SLA window on top).
  // Task #4460 — added "priority" (urgent → low) and "property" (A–Z) so hosts
  // can triage by importance or focus on one building at a time.
  const [sortOrder, setSortOrder] = useLocalStorageState<
    "oldest" | "newest" | "due" | "age" | "priority" | "property"
  >(
    "pp:tasks-sort",
    "oldest",
    (s) =>
      s === "newest" ||
      s === "due" ||
      s === "age" ||
      s === "priority" ||
      s === "property"
        ? s
        : "oldest",
  );
  // Which month the "paid" summary count reflects. 0 = this month, 1 = last
  // month, etc. Lets hosts look back at prior months for retrospective
  // reporting instead of being locked to the live calendar month (Task #2476).
  const [paidMonthOffset, setPaidMonthOffset] = useListState("tasks:paid-month", 0);

  // Instant-apply cache from localStorage so the picker renders immediately on
  // load; the server value (below) is the source of truth and follows the host
  // across devices/browsers (Task #2473).
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = window.localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      const validIds = new Set(COLUMNS.map((c) => c.id));
      return parsed.filter((id): id is string => typeof id === "string" && validIds.has(id));
    } catch {
      return [];
    }
  });

  function cacheHiddenColumns(cols: string[]) {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(cols));
    } catch {
      // Ignore persistence errors (e.g. storage disabled / quota exceeded).
    }
  }

  // Tracks the latest hidden-column value synchronously so rapid toggles never
  // operate on a stale closure, and records whether the user has made a local
  // edit so a late-arriving server hydration can't clobber their choice.
  const hiddenColumnsRef = useRef<string[]>(hiddenColumns);
  const hiddenColumnsHydratedRef = useRef(false);
  const hiddenColumnsEditedRef = useRef(false);

  const hiddenColumnsQuery = useQuery<{ hiddenColumns: string[] }>({
    queryKey: ["/api/app/task-board-columns"],
  });
  const hiddenColumnsMutation = useMutation({
    mutationFn: async (cols: string[]) => {
      const res = await apiRequest("PUT", "/api/app/task-board-columns", { hiddenColumns: cols });
      return (await res.json()) as { hiddenColumns: string[] };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/app/task-board-columns"], data);
    },
  });

  // Once the server value arrives, hydrate local state from it (server wins) —
  // but never overwrite a choice the user already made locally while the initial
  // request was still in flight.
  useEffect(() => {
    const serverData = hiddenColumnsQuery.data;
    if (!serverData || hiddenColumnsHydratedRef.current || hiddenColumnsEditedRef.current) return;
    hiddenColumnsHydratedRef.current = true;
    const validIds = new Set(COLUMNS.map((c) => c.id));
    const next = Array.isArray(serverData.hiddenColumns)
      ? serverData.hiddenColumns.filter((id): id is string => typeof id === "string" && validIds.has(id))
      : [];
    hiddenColumnsRef.current = next;
    setHiddenColumns(next);
    cacheHiddenColumns(next);
  }, [hiddenColumnsQuery.data]);

  function persistHiddenColumns(next: string[]) {
    hiddenColumnsEditedRef.current = true;
    hiddenColumnsRef.current = next;
    setHiddenColumns(next);
    cacheHiddenColumns(next);
    hiddenColumnsMutation.mutate(next);
  }

  function toggleColumn(id: string) {
    const current = hiddenColumnsRef.current;
    persistHiddenColumns(
      current.includes(id)
        ? current.filter((c) => c !== id)
        : [...current, id],
    );
  }

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => !hiddenColumns.includes(c.id)),
    [hiddenColumns],
  );

  const [dragOver, setDragOver] = useState<string | null>(null);
  const [dragTaskId, setDragTaskId] = useState<number | null>(null);
  // Task #4284 — task being closed via drag into Paid / Closed; drives the
  // Approve Payment popup (charge OR manual / $0).
  const [payCloseTask, setPayCloseTask] = useState<MaintenanceTask | null>(null);
  const [pushErrorsOpen, setPushErrorsOpen] = useState(false);
  const journeyStage = useJourneyStage();

  const tasksQuery = useQuery<MaintenanceTaskWithLinkedChats[]>({ queryKey: ["/api/app/tasks"] });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const teamQuery = useQuery<TeamMember[]>({ queryKey: ["/api/app/team-members"] });
  const vendorsQuery = useQuery<Vendor[]>({ queryKey: ["/api/app/operations/vendors"] });
  const settingsQuery = useQuery<MaintenanceSettings>({ queryKey: ["/api/app/tasks/settings"] });
  // Task #3890 — Surface owner-approval status on every board card. Approvals
  // only exist for tasks with a resolvable owner above the pre-approved
  // threshold, so keying badges off this map automatically hides the badge for
  // tasks that don't qualify.
  const ownerApprovalsQuery = useQuery<OwnerVendorApproval[]>({ queryKey: ["/api/app/owner-vendor-approvals"] });
  const ownerApprovalsByTaskId = useMemo(() => {
    const map = new Map<number, OwnerVendorApproval>();
    for (const a of ownerApprovalsQuery.data || []) {
      map.set(a.taskId, a);
    }
    return map;
  }, [ownerApprovalsQuery.data]);
  // Task #1425 — used to resolve reporter avatars on Kanban cards. Pulls all
  // members the host can reach (no propertyId) so we can look up by id.
  const allMembersQuery = useQuery<ReporterMember[]>({ queryKey: ["/api/app/devices/members"] });
  const membersById = useMemo(() => {
    const map = new Map<number, ReporterMember>();
    for (const m of (allMembersQuery.data || [])) map.set(m.id, m);
    return map;
  }, [allMembersQuery.data]);

  const allTasks = tasksQuery.data || [];
  const allProperties = propertiesQuery.data || [];
  const searchPropertiesById = useMemo(() => new Map(allProperties.map(property => [property.id, property])), [allProperties]);
  // Non-archived filter: tasks can be assigned to any property that isn't archived
  // (active, underwriting, launch, acquisition, etc.). Active-only would hide valid targets.
  const properties = sortProperties(allProperties.filter((p) => p.status !== "archive"));
  const teamMembers = (teamQuery.data || []).filter((m) => m.status === "accepted");
  const allVendors = vendorsQuery.data || [];
  const slaConfig = settingsQuery.data?.slaConfig as { urgent: number; high: number; medium: number; low: number } | null | undefined;

  const allFailedPushTasks = useMemo(() => {
    return allTasks.filter((t) => t.padsplitPushError != null && t.padsplitPushError.length > 0);
  }, [allTasks]);

  const failedPushCountByProperty = useMemo(() => {
    const counts = new Map<number, number>();
    for (const t of allFailedPushTasks) {
      if (t.propertyId != null) {
        counts.set(t.propertyId, (counts.get(t.propertyId) ?? 0) + 1);
      }
    }
    return counts;
  }, [allFailedPushTasks]);

  const failedPushTasks = useMemo(() => {
    if (propertyFilter === "all") return allFailedPushTasks;
    const propId = parseInt(propertyFilter, 10);
    return allFailedPushTasks.filter((t) => t.propertyId === propId);
  }, [allFailedPushTasks, propertyFilter]);

  const [pushErrorsAutoOpened, setPushErrorsAutoOpened] = useState(false);
  useEffect(() => {
    if (!pushErrorsAutoOpened && allFailedPushTasks.length > 0) {
      setPushErrorsOpen(true);
      setPushErrorsAutoOpened(true);
    }
  }, [allFailedPushTasks.length, pushErrorsAutoOpened]);

  const filteredTasks = useMemo(() => {
    let result = allTasks;
    if (roomTurnsOnly) {
      result = result.filter((t) => t.category === "Room Turn");
    }
    if (activeOnly) {
      result = result.filter((t) => isTaskTimerActive(t));
    }
    if (formsOnly) {
      result = result.filter((t) => t.formTemplateId != null);
    }
    if (cleansOnly) {
      result = result.filter((t) => t.type === "cleaner_visit");
    }
    if (propertyFilter !== "all") {
      result = result.filter((t) => t.propertyId === parseInt(propertyFilter, 10));
    }
    if (categoryFilter !== "all") {
      result = result.filter((t) => t.category === categoryFilter);
    }
    if (assigneeFilter !== "all") {
      result = result.filter((t) => t.assigneeId === parseInt(assigneeFilter, 10));
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (t) =>
          matchesSearch(q, [
            t.title, t.description, t.id, t.padsplitTaskId, t.category, t.status, t.priority,
            searchPropertiesById.get(t.propertyId ?? -1)?.name,
            searchPropertiesById.get(t.propertyId ?? -1)?.address,
            "task tasks maintenance ticket tickets",
          ])
      );
    }
    return result;
  }, [allTasks, searchPropertiesById, roomTurnsOnly, activeOnly, formsOnly, cleansOnly, propertyFilter, categoryFilter, assigneeFilter, searchText]);

  // Task #4447 / #4457 — client-side ordering shared by the board columns and
  // the list/table view, controlled by the persisted `sortOrder` toolbar
  // control. Sorted client-side so the server's default desc order (consumed
  // elsewhere) is untouched. "oldest"/"newest" key off createdAt; "due" sorts
  // by due date with undated tasks last; "age" surfaces SLA breaches first by
  // ordering on how deep each task is into (or past) its SLA window.
  const sortedFilteredTasks = useMemo(() => {
    const byCreatedAsc = (a: MaintenanceTask, b: MaintenanceTask) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    const slaProgress = (t: MaintenanceTask): number => {
      // Higher = closer to / further past the SLA deadline. Terminal or
      // SLA-less tasks (no config, batch priority, unknown priority) sort to
      // the bottom via -Infinity so live breaches rise to the top.
      if (TERMINAL_STATUSES.includes(t.status)) return -Infinity;
      const slaHours = slaConfig?.[t.priority as keyof typeof slaConfig];
      if (!slaHours) return -Infinity;
      const elapsed = differenceInHours(new Date(), new Date(t.createdAt));
      return elapsed / slaHours;
    };
    // Task #4460 — priority rank (urgent first); unknown/batch sink to the
    // bottom so the actionable tiers rise to the top.
    const PRIORITY_RANK: Record<string, number> = {
      urgent: 0,
      high: 1,
      medium: 2,
      low: 3,
      batch: 4,
    };
    const priorityRank = (t: MaintenanceTask) =>
      PRIORITY_RANK[t.priority] ?? Number.POSITIVE_INFINITY;
    // Task #4460 — property name lookup for the A–Z sort; tasks with no
    // property fall to the bottom.
    const propertyName = (t: MaintenanceTask) =>
      t.propertyId
        ? properties.find((p) => p.id === t.propertyId)?.name ?? ""
        : "";
    const sorted = [...filteredTasks];
    switch (sortOrder) {
      case "newest":
        return sorted.sort((a, b) => byCreatedAsc(b, a));
      case "priority":
        return sorted.sort((a, b) => {
          const ra = priorityRank(a);
          const rb = priorityRank(b);
          if (ra === rb) return byCreatedAsc(a, b);
          return ra - rb;
        });
      case "property":
        return sorted.sort((a, b) => {
          const na = propertyName(a);
          const nb = propertyName(b);
          if (!na && !nb) return byCreatedAsc(a, b);
          if (!na) return 1;
          if (!nb) return -1;
          return na.localeCompare(nb) || byCreatedAsc(a, b);
        });
      case "due":
        return sorted.sort((a, b) => {
          const da = a.dueDate ? new Date(a.dueDate).getTime() : null;
          const db = b.dueDate ? new Date(b.dueDate).getTime() : null;
          if (da === null && db === null) return byCreatedAsc(a, b);
          if (da === null) return 1;
          if (db === null) return -1;
          return da - db || byCreatedAsc(a, b);
        });
      case "age":
        return sorted.sort((a, b) => {
          const pb = slaProgress(b);
          const pa = slaProgress(a);
          if (pa === pb) return byCreatedAsc(a, b);
          return pb - pa;
        });
      case "oldest":
      default:
        return sorted.sort(byCreatedAsc);
    }
  }, [filteredTasks, sortOrder, slaConfig, properties]);

  // Month options for the "paid" summary window: this month plus the prior 11
  // calendar months, so hosts can pull retrospective totals (Task #2476).
  const paidMonthOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, offset) => {
      const d = subMonths(now, offset);
      const label =
        offset === 0 ? "This month" : offset === 1 ? "Last month" : format(d, "MMMM yyyy");
      return { offset, label, year: d.getFullYear(), month: d.getMonth() };
    });
  }, []);

  const selectedPaidMonth =
    paidMonthOptions.find((o) => o.offset === paidMonthOffset) ?? paidMonthOptions[0];
  // Reference date (1st of the selected month) used to bucket "paid" tasks via
  // isTaskPaidInMonth, which compares against the ref's month/year.
  const selectedPaidMonthRef = useMemo(
    () => new Date(selectedPaidMonth.year, selectedPaidMonth.month, 1),
    [selectedPaidMonth.year, selectedPaidMonth.month],
  );

  const boardSummary = useMemo(() => {
    let awaitingApproval = 0;
    let pendingCents = 0;
    let paidInMonth = 0;
    for (const t of filteredTasks) {
      if (t.status === "awaiting_approval") awaitingApproval += 1;
      if (PAYMENT_PENDING_STATUSES.includes(t.status as any)) {
        pendingCents += t.priceCents ?? 0;
      }
      // Task #2477 — prefer the real paid-at timestamp; fall back to
      // updatedAt only for legacy paid tasks (centralized in isTaskPaidInMonth).
      // Task #2476 — bucket against the host-selected month, not a hardcoded now.
      if (isTaskPaidInMonth(t, selectedPaidMonthRef)) {
        paidInMonth += 1;
      }
    }
    return { awaitingApproval, pendingCents, paidInMonth };
  }, [filteredTasks, selectedPaidMonthRef]);

  const activeCategories = useMemo(() => {
    const cats = new Set<string>();
    allTasks.forEach((t) => { if (t.category) cats.add(t.category); });
    return Array.from(cats).sort();
  }, [allTasks]);

  const selectedPropertyName = useMemo(() => {
    if (propertyFilter === "all") {
      const total = allFailedPushTasks.length;
      return total > 0
        ? `Property (All ${properties.length}) — ${total} PadSplit error${total === 1 ? "" : "s"}`
        : `Property (All ${properties.length})`;
    }
    const p = properties.find((pr) => pr.id === parseInt(propertyFilter, 10));
    if (!p) return "Property";
    const count = failedPushCountByProperty.get(p.id) ?? 0;
    return count > 0 ? `${p.name} — ${count} PadSplit error${count === 1 ? "" : "s"}` : p.name;
  }, [propertyFilter, properties, allFailedPushTasks.length, failedPushCountByProperty]);

  const hasActiveFilters = categoryFilter !== "all" || assigneeFilter !== "all" || searchText.trim() !== "";

  function clearFilters() {
    setCategoryFilter("all");
    setAssigneeFilter("all");
    setSearchText("");
  }

  const form = useForm<CreateTaskForm>({
    resolver: zodResolver(createTaskSchema),
    defaultValues: {
      title: "",
      description: "",
      status: "requests",
      priority: "medium",
      source: "manual",
      category: null,
      propertyId: initialPropertyId ? parseInt(initialPropertyId, 10) : undefined,
      assigneeId: null,
      vendorId: null,
      reporterMemberId: null,
      linkedApplianceId: null,
      dueDate: null,
      syncToPadsplit: true,
    },
  });

  const watchedPropertyId = form.watch("propertyId");
  const watchedCategory = form.watch("category");
  // Task #2066 follow-up — when category="Appliances", surface appliances
  // belonging to the selected property so the host can link the task.
  const appliancesQuery = useQuery<Appliance[]>({
    queryKey: ["/api/app/properties", watchedPropertyId, "appliances"],
    queryFn: async () => {
      if (!watchedPropertyId) return [];
      const res = await fetch(`/api/app/properties/${watchedPropertyId}/appliances`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load appliances");
      return res.json();
    },
    enabled: !!watchedPropertyId && createOpen && watchedCategory === "Appliances",
  });
  const propertyAppliances = appliancesQuery.data || [];
  // Clear linkedApplianceId whenever category drifts away from Appliances or
  // the selected property changes, so we don't post a stale FK.
  const prevApplCtxRef = useRef<{ cat: string | null | undefined; pid: number | null | undefined }>({ cat: watchedCategory, pid: watchedPropertyId });
  useEffect(() => {
    const prev = prevApplCtxRef.current;
    if (prev.cat !== watchedCategory || prev.pid !== watchedPropertyId) {
      if (watchedCategory !== "Appliances" && form.getValues("linkedApplianceId") != null) {
        form.setValue("linkedApplianceId", null, { shouldDirty: true });
      } else if (prev.pid !== watchedPropertyId && form.getValues("linkedApplianceId") != null) {
        form.setValue("linkedApplianceId", null, { shouldDirty: true });
      }
      prevApplCtxRef.current = { cat: watchedCategory, pid: watchedPropertyId };
    }
  }, [watchedCategory, watchedPropertyId, form]);
  // Task #1425 — Reporter members are scoped to a single property, so any
  // property change must clear the previously-picked reporter to keep the
  // dropdown valid and avoid sending a stale FK to the server.
  const prevPropertyIdRef = useRef<number | null | undefined>(watchedPropertyId);
  useEffect(() => {
    if (prevPropertyIdRef.current !== watchedPropertyId) {
      if (form.getValues("reporterMemberId") != null) {
        form.setValue("reporterMemberId", null, { shouldDirty: true });
      }
      prevPropertyIdRef.current = watchedPropertyId;
    }
  }, [watchedPropertyId, form]);
  // When the category switches to Room Turn, only cleaning-role vendors remain
  // eligible — drop any previously-selected non-cleaning vendor so we don't
  // post an ineligible vendor that's no longer visible in the dropdown.
  useEffect(() => {
    if (watchedCategory !== "Room Turn") return;
    const selectedId = form.getValues("vendorId");
    if (selectedId == null) return;
    const selected = allVendors.find((v) => v.id === selectedId);
    const roles = selected && Array.isArray(selected.roles) ? (selected.roles as string[]) : [];
    if (!roles.includes("cleaning")) {
      form.setValue("vendorId", null, { shouldDirty: true });
    }
  }, [watchedCategory, allVendors, form]);
  const reporterMembersQuery = useQuery<ReporterMember[]>({
    queryKey: ["/api/app/tasks/property-members", watchedPropertyId],
    queryFn: async () => {
      if (!watchedPropertyId) return [];
      const res = await fetch(`/api/app/tasks/property-members?propertyId=${watchedPropertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load members");
      return res.json();
    },
    enabled: !!watchedPropertyId && createOpen,
  });
  const reporterMembers = reporterMembersQuery.data || [];
  const watchedProperty = watchedPropertyId ? properties.find((p) => p.id === watchedPropertyId) : null;
  const showPadsplitToggle = !!watchedProperty?.padsplitPropertyId;
  // Room Turn tasks are cleaning jobs, so only vendors carrying the "cleaning"
  // role (VENDOR_ROLES) are eligible; every other category shows all
  // property-scoped vendors.
  const filteredVendors = (watchedPropertyId
    ? allVendors.filter((v) => !v.propertyIds || (v.propertyIds as number[]).length === 0 || (v.propertyIds as number[]).includes(watchedPropertyId))
    : allVendors
  ).filter((v) =>
    watchedCategory === "Room Turn"
      ? (Array.isArray(v.roles) ? (v.roles as string[]) : []).includes("cleaning")
      : true,
  );

  // Task #2650 — AI title/description suggestion in the create flow. There are
  // no linked chats yet, so we draft from the typed description (free text).
  // The suggested title is offered via the same "Apply suggested title" pill
  // used in the edit view; the PM accepts or ignores it independently.
  const [suggestedTitle, setSuggestedTitle] = useState<string | null>(null);
  const draftDescriptionMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/operations/tasks/draft-description", {
        title: form.getValues("title") || "",
        description: form.getValues("description") || "",
        category: form.getValues("category") || null,
        propertyId: form.getValues("propertyId") ?? null,
      });
      return res.json() as Promise<{ description: string; suggestedTitle?: string }>;
    },
    onSuccess: (data) => {
      form.setValue("description", data.description, { shouldDirty: true });
      form.clearErrors("description");
      setSuggestedTitle((data.suggestedTitle || "").trim() || null);
      toast({ title: "AI description drafted", description: "Review and edit before saving." });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't draft description", description: parseErrorMessage(e, "Try again in a moment."), variant: "destructive" }),
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateTaskForm & { sendToVendor?: boolean }) => {
      const property = data.propertyId ? properties.find((p) => p.id === data.propertyId) : null;
      const eligibleForPadsplit = !!property?.padsplitPropertyId;
      const res = await apiRequest("POST", "/api/app/tasks", {
        ...data,
        dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : null,
        propertyId: data.propertyId || null,
        assigneeId: data.assigneeId != null ? data.assigneeId : null,
        vendorId: data.vendorId || null,
        // Task #1612 — opt-in flag for "Save & send to vendor". Backend
        // ignores it if no vendorId is set.
        sendToVendor: data.sendToVendor === true,
        // Only meaningful when the property has a PadSplit ID; backend ignores otherwise.
        syncToPadsplit: eligibleForPadsplit ? data.syncToPadsplit !== false : true,
      });
      return res.json();
    },
    onSuccess: (newTask: MaintenanceTask & { vendorNotify?: { sms: boolean; email: boolean; reason?: string } }) => {
      const { vendorNotify, ...task } = newTask;
      const taskWithCount: MaintenanceTaskWithLinkedChats = { ...(task as MaintenanceTask), linkedChatCount: 0, unreadVendorMessageCount: 0, activeTimerStartedAt: null };
      queryClient.setQueryData<MaintenanceTaskWithLinkedChats[]>(["/api/app/tasks"], (old) =>
        old ? [taskWithCount, ...old] : [taskWithCount]
      );
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      setCreateOpen(false);
      form.reset();
      setSuggestedTitle(null);
      // Compose a single toast that reflects the vendor outcome when present.
      const parts: string[] = [];
      let destructive = false;
      let description: string | undefined;
      if (vendorNotify) {
        const channels = [vendorNotify.sms ? "SMS" : null, vendorNotify.email ? "email" : null].filter(Boolean).join(" + ");
        if (channels) {
          parts.push(`sent to vendor (${channels})`);
        } else {
          parts.push("couldn't reach vendor");
          destructive = true;
          description = description || "We saved the task but couldn't deliver SMS or email. Check the vendor's contact info.";
        }
      }
      toast({
        title: parts.length ? `Task created — ${parts.join(", ")}` : "Task created successfully",
        description,
        variant: destructive ? "destructive" : "default",
      });
    },
    onError: (e: Error) => {
      const fieldErrors = parseFieldErrors(e);
      const fieldKeys = Object.keys(fieldErrors);
      if (fieldKeys.length > 0) {
        const formFieldNames = new Set([
          "title",
          "description",
          "category",
          "priority",
          "status",
          "propertyId",
          "assigneeId",
          "vendorId",
          "reporterMemberId",
          "dueDate",
          "source",
          "padsplitTaskId",
        ]);
        let appliedToForm = false;
        for (const key of fieldKeys) {
          if (formFieldNames.has(key)) {
            form.setError(key as keyof CreateTaskForm, {
              type: "server",
              message: fieldErrors[key],
            });
            appliedToForm = true;
          }
        }
        if (appliedToForm) {
          toast({
            title: "Please fix the highlighted fields",
            description: Object.values(fieldErrors).join(" "),
            variant: "destructive",
          });
          return;
        }
      }
      toast({ title: "Failed to create task", description: parseErrorMessage(e, "Couldn't create task. Please try again."), variant: "destructive" });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ taskId, status }: { taskId: number; status: string }) => {
      return apiRequest("PATCH", `/api/app/tasks/${taskId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
    },
    onError: () => {
      toast({ title: "Failed to update task status", variant: "destructive" });
    },
  });

  function handleDragStart(e: React.DragEvent, taskId: number) {
    setDragTaskId(taskId);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e: React.DragEvent, colId: string) {
    // Block dropping onto payment-driven columns — those transitions are
    // controlled by the approval/payment flow, not manual drag-and-drop.
    // Task #4284 — the Paid / Closed column is a special drop target: dropping
    // there opens the Approve Payment popup (charge OR manual / $0) instead of a
    // plain status PATCH.
    const col = COLUMNS.find((c) => c.id === colId);
    if (colId === "paid_closed") {
      // Only Complete / Payment-Pending tasks may be dropped here to open the
      // close popup; reject the drop target for any other source status.
      const dragged = dragTaskId ? allTasks.find((t) => t.id === dragTaskId) : undefined;
      if (!dragged || !CLOSABLE_SOURCE_STATUSES.includes(dragged.status)) return;
      e.preventDefault();
      setDragOver(colId);
      return;
    }
    if (!col?.draggableTarget) return;
    e.preventDefault();
    setDragOver(colId);
  }

  function handleDrop(e: React.DragEvent, colId: string) {
    e.preventDefault();
    const col = COLUMNS.find((c) => c.id === colId);
    // Task #4284 — Paid / Closed: open the close popup for the dragged task; the
    // task only moves once a payment/archive record is created in the popup.
    if (colId === "paid_closed") {
      if (dragTaskId) {
        const task = allTasks.find((t) => t.id === dragTaskId);
        // Only Complete / Payment-Pending tasks open the close popup; dragging
        // from any other column into Paid / Closed is a no-op.
        if (task && CLOSABLE_SOURCE_STATUSES.includes(task.status)) {
          setPayCloseTask(task);
        }
      }
      setDragOver(null);
      setDragTaskId(null);
      return;
    }
    if (!col?.draggableTarget) {
      setDragOver(null);
      setDragTaskId(null);
      return;
    }
    if (dragTaskId && colId) {
      const task = allTasks.find((t) => t.id === dragTaskId);
      // Prevent dragging payment-lifecycle tasks out of their column via drag.
      if (task && PAYMENT_DRIVEN_STATUSES.includes(task.status)) {
        setDragOver(null);
        setDragTaskId(null);
        return;
      }
      if (task && !col.statuses.includes(task.status)) {
        // The column's primary drop-target status is the first in the statuses array.
        updateStatusMutation.mutate({ taskId: dragTaskId, status: col.statuses[0] });
      }
    }
    setDragOver(null);
    setDragTaskId(null);
  }

  return (
    <AppLayout>
      <div className="p-6 h-full flex flex-col">
        <ViewOnlyBanner area="maintenance" />
        {initialPropertyId ? (
          <BackToPropertyLink propertyId={initialPropertyId} tab="maintenance" />
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-y-3 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900" data-tour="maintenance-heading">Tasks</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = "/app/maintenance/settings"}
              data-testid="button-maintenance-settings"
            >
              <Settings className="h-4 w-4 mr-1.5" />
              Settings
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 border-gray-300"
                  data-testid="button-column-visibility"
                >
                  <Columns3 className="h-4 w-4 mr-1.5" />
                  Columns
                  {hiddenColumns.length > 0 && (
                    <Badge variant="secondary" className="ml-1.5" data-testid="badge-hidden-columns">
                      {hiddenColumns.length} hidden
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.map((col) => {
                  const checked = !hiddenColumns.includes(col.id);
                  const isLastVisible = checked && visibleColumns.length === 1;
                  return (
                    <DropdownMenuCheckboxItem
                      key={col.id}
                      checked={checked}
                      disabled={isLastVisible}
                      onSelect={(e) => e.preventDefault()}
                      onCheckedChange={() => toggleColumn(col.id)}
                      data-testid={`checkbox-column-${col.id}`}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  );
                })}
                {hiddenColumns.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <button
                      onClick={() => persistHiddenColumns([])}
                      className="w-full text-left text-sm text-brand hover:underline px-2 py-1.5"
                      data-testid="button-show-all-columns"
                    >
                      Show all columns
                    </button>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="button-create-task" data-tour="maintenance-add" disabled={readOnly}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add Task
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="maintenance" />
              <GuideButton pageKey="maintenance" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="maintenance" />
        </div>

        <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="room-turns-toggle-bar">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 mr-1" data-testid="label-quick-filters">
            Quick filters
          </span>
          <button
            onClick={() => setQuickFilter("all")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${quickFilter === "all" ? "bg-brand text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid="button-filter-all-tasks"
          >
            All
          </button>
          <button
            onClick={() => setQuickFilter("room_turns")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${roomTurnsOnly ? "bg-brand text-white" : "bg-brand-muted text-brand-muted-foreground hover:bg-brand"} hover:text-white`}
            data-testid="button-filter-room-turns"
          >
            Room Turns only
          </button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setQuickFilter("active")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeOnly ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                data-testid="button-filter-active-tasks"
              >
                <Timer className="h-3.5 w-3.5" />
                Active Tasks
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Tasks with a vendor work timer currently running
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setQuickFilter("forms")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${formsOnly ? "bg-brand text-white" : "bg-brand-muted text-brand-muted-foreground hover:bg-brand hover:text-white"}`}
                data-testid="button-filter-form-tasks"
              >
                <ClipboardCheck className="h-3.5 w-3.5" />
                Forms
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Tasks carrying a service form (form-tasks)
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setQuickFilter("cleans")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${cleansOnly ? "bg-violet-600 text-white" : "bg-violet-50 text-violet-700 hover:bg-violet-100"}`}
                data-testid="button-filter-clean-tasks"
              >
                <SprayCan className="h-3.5 w-3.5" />
                Cleans
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Scheduled third-party cleaner visits
            </TooltipContent>
          </Tooltip>
          <div className="ml-auto flex items-center gap-2">
            <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as typeof sortOrder)}>
              <SelectTrigger className="w-auto min-w-[160px] gap-2 border-gray-300 h-9 px-3" data-testid="select-task-sort">
                <ArrowUpDown className="h-4 w-4 text-gray-500" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="oldest" data-testid="select-sort-oldest">Oldest first</SelectItem>
                <SelectItem value="newest" data-testid="select-sort-newest">Newest first</SelectItem>
                <SelectItem value="due" data-testid="select-sort-due">Due date</SelectItem>
                <SelectItem value="age" data-testid="select-sort-age">Age since started</SelectItem>
                <SelectItem value="priority" data-testid="select-sort-priority">Priority (urgent first)</SelectItem>
                <SelectItem value="property" data-testid="select-sort-property">Property (A–Z)</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1 rounded-md border border-gray-300 p-0.5" data-testid="view-toggle">
            <button
              type="button"
              onClick={() => setView("board")}
              aria-pressed={view === "board"}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-sm font-medium transition-colors ${view === "board" ? "bg-brand text-white" : "text-gray-600 hover:bg-gray-100"}`}
              data-testid="button-view-board"
            >
              <LayoutGrid className="h-4 w-4" />
              Board
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              aria-pressed={view === "list"}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-sm font-medium transition-colors ${view === "list" ? "bg-brand text-white" : "text-gray-600 hover:bg-gray-100"}`}
              data-testid="button-view-list"
            >
              <List className="h-4 w-4" />
              List
            </button>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <button
            className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline mb-3"
            onClick={() => setFiltersOpen(!filtersOpen)}
            data-testid="button-toggle-filters"
          >
            {filtersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Filter
          </button>
          {filtersOpen && (
            <div className="flex items-center gap-3 flex-wrap" data-testid="filter-bar" data-tour="maintenance-filters">
              <SearchableSelect
                value={propertyFilter}
                onValueChange={setPropertyFilter}
                placeholder={selectedPropertyName}
                searchPlaceholder="Search properties..."
                emptyText="No properties match."
                testId="select-property-filter"
                className="w-auto min-w-[200px] gap-2 border-gray-300 h-9 px-3"
                leadingIcon={<Building2 className="h-4 w-4 text-gray-500" />}
                options={[
                  {
                    value: "all",
                    label: `All ${properties.length} Properties`,
                    testId: "select-property-option-all",
                    labelNode: (
                      <span className="inline-flex items-center justify-between gap-3 w-full">
                        <span>All {properties.length} Properties</span>
                        {allFailedPushTasks.length > 0 && (
                          <span
                            className="inline-flex items-center whitespace-nowrap rounded-md bg-red-600 px-2 py-0.5 text-xs font-semibold text-white ml-2"
                            data-testid="badge-property-option-failures-all"
                          >
                            {allFailedPushTasks.length} error{allFailedPushTasks.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </span>
                    ),
                  },
                  ...properties.map((p) => {
                    const failCount = failedPushCountByProperty.get(p.id) ?? 0;
                    return {
                      value: String(p.id),
                      label: p.name,
                      testId: `select-property-option-${p.id}`,
                      labelNode: (
                        <span className="inline-flex items-center justify-between gap-3 w-full">
                          <span className="inline-flex items-center gap-2 min-w-0">
                            {failCount > 0 && (
                              <span
                                className="inline-block h-2 w-2 rounded-full bg-red-600 shrink-0"
                                aria-hidden="true"
                                data-testid={`dot-property-option-failures-${p.id}`}
                              />
                            )}
                            <span className="truncate">{p.name}</span>
                          </span>
                          {failCount > 0 && (
                            <span
                              className="inline-flex items-center whitespace-nowrap rounded-md bg-red-600 px-2 py-0.5 text-xs font-semibold text-white ml-2"
                              data-testid={`badge-property-option-failures-${p.id}`}
                            >
                              {failCount}
                            </span>
                          )}
                        </span>
                      ),
                    };
                  }),
                ]}
              />
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[200px] h-9 border-gray-300" data-testid="select-category-filter">
                  <SelectValue placeholder="Category (not selected)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Category (not selected)</SelectItem>
                  {MAINTENANCE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                  {activeCategories.filter((c) => !(MAINTENANCE_CATEGORIES as readonly string[]).includes(c)).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className="w-[200px] h-9 border-gray-300" data-testid="select-assignee-filter">
                  <SelectValue placeholder="Assignee (not selected)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Assignee (not selected)</SelectItem>
                  {teamMembers.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search"
                  className="w-[200px] h-9 border-gray-300"
                  data-testid="input-search-filter"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {searchText.length} / 50
                </span>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md px-3 py-1.5"
                  data-testid="button-clear-filters"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mb-4" data-testid="section-padsplit-failures">
          <button
            type="button"
            onClick={() => setPushErrorsOpen((v) => !v)}
            className={`w-full flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors ${
              failedPushTasks.length > 0
                ? "border-red-200 bg-red-50 hover:bg-red-100"
                : "border-gray-200 bg-gray-50 hover:bg-gray-100"
            }`}
            data-testid="button-toggle-padsplit-failures"
          >
            <div className="flex items-center gap-2">
              {failedPushTasks.length > 0 ? (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-gray-500" />
              )}
              <span className={`text-sm font-medium ${failedPushTasks.length > 0 ? "text-red-800" : "text-gray-700"}`} data-testid="text-padsplit-failures-title">
                PadSplit push errors
              </span>
              <Badge
                className={failedPushTasks.length > 0 ? "bg-red-600 text-white" : "bg-gray-200 text-gray-700"}
                data-testid="badge-padsplit-failure-count"
              >
                {failedPushTasks.length}
              </Badge>
            </div>
            {pushErrorsOpen ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </button>
          {pushErrorsOpen && (
            <div className="mt-2 rounded-md border border-gray-200 bg-white" data-testid="panel-padsplit-failures">
              {failedPushTasks.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500" data-testid="text-padsplit-failures-empty">
                  {propertyFilter !== "all" && allFailedPushTasks.length > 0
                    ? `No PadSplit push errors for this property. ${allFailedPushTasks.length} failure${allFailedPushTasks.length === 1 ? "" : "s"} for other properties.`
                    : "No PadSplit push errors. All eligible tasks are in sync."}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
                    <p className="text-xs text-gray-600" data-testid="text-padsplit-failures-summary">
                      {failedPushTasks.length} task{failedPushTasks.length === 1 ? "" : "s"} failed to push to PadSplit
                      {propertyFilter !== "all" && allFailedPushTasks.length > failedPushTasks.length
                        ? ` for this property (${allFailedPushTasks.length} total across all properties)`
                        : ""}
                      .
                    </p>
                  </div>
                  <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                    {failedPushTasks.map((t) => {
                      const property = t.propertyId ? properties.find((p) => p.id === t.propertyId) : null;
                      return (
                        <li
                          key={t.id}
                          className="flex items-start gap-3 px-3 py-2 hover:bg-gray-50"
                          data-testid={`row-padsplit-failure-${t.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedTaskId(t.id)}
                            className="flex-1 min-w-0 text-left"
                            data-testid={`link-padsplit-failure-task-${t.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate" data-testid={`text-padsplit-failure-title-${t.id}`}>
                                {t.title}
                              </span>
                              <Badge className={`text-xs ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</Badge>
                            </div>
                            {property && (
                              <p className="text-xs text-gray-500 truncate" data-testid={`text-padsplit-failure-property-${t.id}`}>
                                {property.name || property.address}
                              </p>
                            )}
                            <p className="text-xs text-red-700 mt-0.5 line-clamp-2" data-testid={`text-padsplit-failure-error-${t.id}`}>
                              {t.padsplitPushError}
                            </p>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <BatchPoolPanel
          properties={properties}
          vendors={allVendors}
          readOnly={readOnly}
          onTaskClick={(id) => setSelectedTaskId(id)}
        />

        {isUpgradeRequiredError(tasksQuery.error) ? (
          <InlineUpgradePanel error={tasksQuery.error} />
        ) : !tasksQuery.isLoading && allTasks.length === 0 ? (
          <div className="flex-1">
            <JourneyEmptyState
              page="maintenance"
              stage={journeyStage}
              icon={<AlertCircle className="h-10 w-10 text-gray-300" />}
              primaryAction={{ onClick: () => setCreateOpen(true) }}
            />
          </div>
        ) : (
        <>
        <div className="mb-4 w-full flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <button
            type="button"
            onClick={() => navigateLoc("/app/operations/approvals")}
            className="flex flex-wrap items-center gap-x-6 gap-y-1 text-left -mx-1 -my-1 px-1 py-1 rounded-md transition-colors hover:bg-gray-50"
            data-testid="button-board-summary"
            title="Go to Vendor Payments"
          >
            <span className="flex items-center gap-1.5 text-sm" data-testid="text-summary-awaiting-approval">
              <span className="font-semibold text-purple-700">{boardSummary.awaitingApproval}</span>
              <span className="text-gray-600">awaiting approval</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex cursor-help text-gray-400 hover:text-gray-600"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="tooltip-awaiting-approval"
                    aria-label="How the awaiting approval count is calculated"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Counts tasks a vendor has completed and submitted that are sitting in the
                  awaiting-approval status, waiting for you to approve payment.
                </TooltipContent>
              </Tooltip>
            </span>
            <span className="text-gray-300" aria-hidden="true">·</span>
            <span className="flex items-center gap-1.5 text-sm" data-testid="text-summary-pending-amount">
              <span className="font-semibold text-purple-700">{formatCents(boardSummary.pendingCents)}</span>
              <span className="text-gray-600">pending</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex cursor-help text-gray-400 hover:text-gray-600"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="tooltip-pending-amount"
                    aria-label="How the pending amount is calculated"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Sums the price of every task still in a payment-pending status — completed,
                  awaiting approval, or approved — i.e. work that's done but not yet paid.
                </TooltipContent>
              </Tooltip>
            </span>
            <span className="text-gray-300" aria-hidden="true">·</span>
            <span className="flex items-center gap-1.5 text-sm" data-testid="text-summary-paid-this-month">
              <span className="font-semibold text-emerald-700">{boardSummary.paidInMonth}</span>
              <span className="text-gray-600">
                paid {paidMonthOffset === 0 ? "this month" : paidMonthOffset === 1 ? "last month" : `in ${selectedPaidMonth.label}`}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex cursor-help text-gray-400 hover:text-gray-600"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="tooltip-paid-this-month"
                    aria-label={`How the ${paidMonthOffset === 0 ? "this month" : selectedPaidMonth.label} paid count is calculated`}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Counts tasks marked paid during{" "}
                  {paidMonthOffset === 0 ? "the current calendar month" : selectedPaidMonth.label}, bucketed by their
                  payment date — not when the task was last edited. A recently edited older task won't appear here.
                </TooltipContent>
              </Tooltip>
            </span>
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <Select
              value={String(paidMonthOffset)}
              onValueChange={(v) => setPaidMonthOffset(Number(v))}
            >
              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="select-paid-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {paidMonthOptions.map((opt) => (
                  <SelectItem key={opt.offset} value={String(opt.offset)} data-testid={`option-paid-month-${opt.offset}`}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => navigateLoc("/app/operations/approvals")}
              className="text-xs font-medium text-brand hover:underline whitespace-nowrap"
              data-testid="link-board-summary-payments"
            >
              Go to Vendor Payments →
            </button>
          </div>
        </div>
        {view === "board" ? (
        <div className="flex gap-4 flex-1 overflow-x-auto pb-4">
          {visibleColumns.map((col) => {
            const colTasks = sortedFilteredTasks.filter((t) => col.statuses.includes(t.status));
            const isPaymentDriven = !col.draggableTarget;
            return (
              <div
                key={col.id}
                className={`flex-1 min-w-[220px] rounded-lg flex flex-col transition-colors ${
                  dragOver === col.id && col.draggableTarget
                    ? "bg-blue-50 dark:bg-blue-950/40"
                    : isPaymentDriven
                    ? "bg-purple-50/60 dark:bg-purple-950/20"
                    : "bg-gray-50 dark:bg-gray-900/40"
                }`}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDrop={(e) => handleDrop(e, col.id)}
                onDragLeave={() => setDragOver(null)}
                data-testid={`column-${col.id}`}
              >
                <div className="px-4 pt-4 pb-2">
                  <h3 className={`font-semibold text-sm ${col.color}`}>
                    {col.label} ({colTasks.length})
                  </h3>
                  {isPaymentDriven && (
                    <p className="text-xs text-gray-400 mt-0.5">Managed by payment flow</p>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto px-3 pb-3">
                  {colTasks.map((task) => {
                    // Task #4284 — Any task that isn't already closed (paid) can be
                    // dragged. Open/in-progress tasks drag between normal columns;
                    // Complete and Completed / Payment Pending tasks can be dragged
                    // into Paid / Closed (which opens the close popup). Paid tasks
                    // are terminal and cannot be dragged out.
                    const canDrag = !PAID_STATUSES.includes(task.status as any);
                    return (
                    <div
                      key={task.id}
                      draggable={canDrag}
                      onDragStart={(e) => {
                        if (canDrag) {
                          handleDragStart(e, task.id);
                        }
                      }}
                    >
                      <TaskCard
                        task={task}
                        properties={properties}
                        teamMembers={teamMembers}
                        membersById={membersById}
                        slaConfig={slaConfig}
                        ownerApproval={ownerApprovalsByTaskId.get(task.id) ?? null}
                        onClick={() => setSelectedTaskId(task.id)}
                      />
                    </div>
                    );
                  })}
                  {colTasks.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-xs">
                      No tasks
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        ) : (
          <div className="flex-1 overflow-auto rounded-lg border border-gray-200 bg-white" data-testid="tasks-table-view">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Timer</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedFilteredTasks.map((task) => {
                  const property = properties.find((p) => p.id === task.propertyId);
                  const assignee = teamMembers.find((m) => m.id === task.assigneeId);
                  const assigneeLabel = assignee ? (assignee.name || assignee.email) : null;
                  const age = getTaskAge(task, slaConfig);
                  const timerActive = isTaskTimerActive(task);
                  return (
                    <TableRow
                      key={task.id}
                      onClick={() => setSelectedTaskId(task.id)}
                      className={`cursor-pointer ${timerActive ? "bg-emerald-50 dark:bg-emerald-950/30" : ""}`}
                      data-testid={`row-task-${task.id}`}
                      data-timer-active={timerActive ? "true" : "false"}
                    >
                      <TableCell className="text-xs text-gray-500" data-testid={`cell-task-id-${task.id}`}>
                        {task.id}
                      </TableCell>
                      <TableCell className="font-medium max-w-[280px]" data-testid={`cell-task-title-${task.id}`}>
                        <span className="line-clamp-2 break-words">{task.title}</span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600" data-testid={`cell-task-property-${task.id}`}>
                        {property?.name ?? "—"}
                      </TableCell>
                      <TableCell data-testid={`cell-task-category-${task.id}`}>
                        {task.category ? (
                          <Badge variant="secondary" className={task.category === "Room Turn" ? "bg-brand-muted text-brand-muted-foreground" : ""}>
                            {task.category}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`cell-task-status-${task.id}`}>
                        {getStatusLabel(task.status)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600" data-testid={`cell-task-assignee-${task.id}`}>
                        {assigneeLabel ?? <span className="text-gray-400">Unassigned</span>}
                      </TableCell>
                      <TableCell data-testid={`cell-task-age-${task.id}`}>
                        {age ? (
                          <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none ${TASK_AGE_COLORS[age.state]}`}>
                            <Clock className="h-2.5 w-2.5" />
                            {age.label}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`cell-task-timer-${task.id}`}>
                        {timerActive && task.activeTimerStartedAt ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                            <Timer className="h-3.5 w-3.5" />
                            <LiveTimer
                              startedAt={task.activeTimerStartedAt}
                              className="font-mono font-semibold"
                              testId={`live-timer-row-${task.id}`}
                            />
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 whitespace-nowrap" data-testid={`cell-task-created-${task.id}`}>
                        {format(new Date(task.createdAt), "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {sortedFilteredTasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-10 text-sm text-gray-400" data-testid="text-no-tasks-list">
                      No tasks
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        </>
        )}
      </div>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          // Gate dismissal while a multi-second AI draft (or the create call)
          // is in flight so a stray click never silently drops the result.
          if (!open && (draftDescriptionMutation.isPending || createMutation.isPending)) return;
          setCreateOpen(open);
          if (!open) setSuggestedTitle(null);
        }}
      >
        <DialogContent
          className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
          data-testid="dialog-create-task"
          onInteractOutside={(e) => { if (draftDescriptionMutation.isPending || createMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (draftDescriptionMutation.isPending || createMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (draftDescriptionMutation.isPending || createMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Create Maintenance Task</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((data) => createMutation.mutate({ ...data, sendToVendor: false }))} className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => {
                  const showSuggestion =
                    !!suggestedTitle &&
                    isGenericTaskTitle(field.value) &&
                    suggestedTitle.trim().toLowerCase() !== (field.value || "").trim().toLowerCase();
                  return (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Title <span className="text-destructive">*</span></FormLabel>
                      {showSuggestion && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[11px] gap-1 text-brand border-brand/30 hover:bg-brand-muted"
                          onClick={() => { form.setValue("title", suggestedTitle!, { shouldDirty: true }); form.clearErrors("title"); }}
                          data-testid="button-apply-suggested-title"
                          title={`Apply suggested title: ${suggestedTitle}`}
                        >
                          <Wand2 className="h-3 w-3" />
                          Apply suggested title
                        </Button>
                      )}
                    </div>
                    <FormControl>
                      <Input placeholder="e.g. Fix leaking faucet in Room 3" {...field} data-testid="input-task-title" />
                    </FormControl>
                    {showSuggestion && (
                      <p className="text-[11px] text-gray-500 mt-1" data-testid="text-suggested-title">
                        Suggested: {suggestedTitle}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => {
                  const hasContext = !!String(field.value ?? "").trim();
                  return (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Description</FormLabel>
                      {hasContext ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[11px] gap-1 text-brand border-brand/30 hover:bg-brand-muted"
                          onClick={() => draftDescriptionMutation.mutate()}
                          disabled={draftDescriptionMutation.isPending}
                          data-testid="button-ai-draft-description"
                        >
                          {draftDescriptionMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Wand2 className="h-3 w-3" />
                          )}
                          {draftDescriptionMutation.isPending
                            ? "Drafting…"
                            : draftDescriptionMutation.isSuccess
                              ? "Rerun"
                              : "AI Draft"}
                        </Button>
                      ) : (
                        <span
                          className="text-[11px] text-gray-400 flex items-center gap-1 cursor-default select-none"
                          title="Type a short description to enable AI drafting"
                          data-testid="hint-ai-draft-no-context"
                        >
                          <Wand2 className="h-3 w-3" />
                          AI Draft (add a description first)
                        </span>
                      )}
                    </div>
                    <FormControl>
                      <Textarea placeholder="Describe the maintenance issue..." rows={3} {...field} value={field.value ?? ""} data-testid="input-task-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                  );
                }}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select onValueChange={(v) => field.onChange(v === "none" ? null : v)} value={field.value || "none"}>
                        <FormControl>
                          <SelectTrigger data-testid="select-task-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No category</SelectItem>
                          {MAINTENANCE_CATEGORIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-task-priority">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="urgent">{priorityWithSla(slaConfig, "urgent")}</SelectItem>
                          <SelectItem value="high">{priorityWithSla(slaConfig, "high")}</SelectItem>
                          <SelectItem value="medium">{priorityWithSla(slaConfig, "medium")}</SelectItem>
                          <SelectItem value="low">{priorityWithSla(slaConfig, "low")}</SelectItem>
                          <SelectItem value="batch">Batch (hold for grouping)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="propertyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Property</FormLabel>
                      <Select onValueChange={(v) => field.onChange(v === "none" ? null : parseInt(v, 10))} value={field.value != null ? String(field.value) : "none"}>
                        <FormControl>
                          <SelectTrigger data-testid="select-task-property">
                            <SelectValue placeholder="Select property" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No property</SelectItem>
                          {properties.map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="assigneeId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assignee</FormLabel>
                      <Select onValueChange={(v) => field.onChange(v === "none" ? null : parseInt(v, 10))} value={field.value != null ? String(field.value) : "none"}>
                        <FormControl>
                          <SelectTrigger data-testid="select-task-assignee">
                            <SelectValue placeholder="Select assignee" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {teamMembers.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {watchedCategory === "Appliances" && (
                <FormField
                  control={form.control}
                  name="linkedApplianceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Linked Appliance</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v === "none" ? null : parseInt(v, 10))}
                        value={field.value != null ? String(field.value) : "none"}
                        disabled={!watchedPropertyId}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-task-linked-appliance">
                            <SelectValue placeholder={watchedPropertyId ? "Select appliance" : "Select a property first"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No appliance</SelectItem>
                          {propertyAppliances.length === 0 && watchedPropertyId ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground" data-testid="text-no-appliances">
                              No appliances on this property yet — add one from the property's Appliances tab.
                            </div>
                          ) : (
                            propertyAppliances.map((a) => (
                              <SelectItem key={a.id} value={String(a.id)} data-testid={`option-linked-appliance-${a.id}`}>
                                {[a.make, a.model].filter(Boolean).join(" ") || `Appliance #${a.id}`}
                                {a.serialNumber ? ` — ${a.serialNumber}` : ""}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="reporterMemberId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reported by (member)</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === "none" ? null : parseInt(v, 10))}
                      value={field.value != null ? String(field.value) : "none"}
                      disabled={!watchedPropertyId}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-task-reporter">
                          <SelectValue placeholder={watchedPropertyId ? "Select member" : "Select a property first"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No member</SelectItem>
                        {reporterMembers.map((m) => (
                          <SelectItem key={m.id} value={String(m.id)} data-testid={`option-reporter-${m.id}`}>
                            {m.displayName || m.name || m.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="vendorId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vendor</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === "none" ? null : parseInt(v, 10))} value={field.value != null ? String(field.value) : "none"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-task-vendor">
                          <SelectValue placeholder="Select vendor" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No vendor</SelectItem>
                        {filteredVendors.map((v) => (
                          <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ""} data-testid="input-task-duedate" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {showPadsplitToggle && (
                <FormField
                  control={form.control}
                  name="syncToPadsplit"
                  render={({ field }) => (
                    <FormItem className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value !== false}
                          onCheckedChange={(v) => field.onChange(v === true)}
                          data-testid="checkbox-sync-to-padsplit"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-medium">Sync this task to PadSplit so the member sees it</FormLabel>
                        <p className="text-xs text-gray-500">Uncheck to keep this task internal-only.</p>
                      </div>
                    </FormItem>
                  )}
                />
              )}
              </div>
              <div className="flex-shrink-0 flex justify-end gap-2 pt-4 border-t mt-2">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit" variant="outline" disabled={createMutation.isPending} data-testid="button-submit-task">
                  {createMutation.isPending ? "Saving..." : (form.watch("vendorId") ? "Save as draft" : "Create Task")}
                </Button>
                {form.watch("vendorId") != null && (
                  <Button
                    type="button"
                    disabled={createMutation.isPending}
                    onClick={form.handleSubmit((data) => createMutation.mutate({ ...data, sendToVendor: true }))}
                    data-testid="button-submit-task-send"
                  >
                    {createMutation.isPending ? "Saving..." : "Save & send to vendor"}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {selectedTaskId && (
        <MaintenanceTaskDetail
          taskId={selectedTaskId}
          properties={properties}
          teamMembers={teamMembers}
          focusCommentId={focusCommentId}
          onClose={() => setSelectedTaskId(null)}
        />
      )}

      <ApproveAndPayModal
        task={(payCloseTask as any) ?? null}
        open={!!payCloseTask}
        onClose={() => setPayCloseTask(null)}
      />
    </AppLayout>
  );
}
